import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, eur, reportUrl, gr, up } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";
import { Boat, FilePdf, CurrencyEur, PencilSimple, Trash, MapPin, Plus } from "@phosphor-icons/react";

export default function Owners() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [detailsOwner, setDetailsOwner] = useState(null);
  const [editOwner, setEditOwner] = useState(null); // { old, name }
  const [deleteOwner, setDeleteOwner] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", date: "", note: "", target: "split" });

  const { data: owners = [], isLoading } = useQuery({
    queryKey: ["owners"],
    queryFn: async () => (await api.get("/owners")).data,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { data: boats = [] } = useQuery({ queryKey: ["boats"], queryFn: async () => (await api.get("/boats")).data });

  const boatsOf = (owner) =>
    boats.filter((b) => String(b.owner || "").trim().toLowerCase() === String(owner || "").trim().toLowerCase());

  const q = search.trim().toLowerCase();
  const filtered = q
    ? owners.filter((o) =>
        String(o.owner || "").toLowerCase().includes(q) ||
        (o.boats || []).some((b) => String(b.name || "").toLowerCase().includes(q))
      )
    : owners;

  const saveRename = async () => {
    const name = (editOwner?.name || "").trim();
    if (!name) return toast.error("Συμπληρώστε όνομα ιδιοκτήτη");
    try {
      await api.put("/owners/rename", { old_owner: editOwner.old, new_owner: name });
      await qc.invalidateQueries();
      toast.success("Ο ιδιοκτήτης ενημερώθηκε");
      setEditOwner(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/owners/${encodeURIComponent(deleteOwner)}`);
      await qc.invalidateQueries();
      toast.success("Ο ιδιοκτήτης διαγράφηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής");
    } finally {
      setDeleteOwner(null);
    }
  };

  const detailBoats = detailsOwner ? boatsOf(detailsOwner.owner) : [];
  const sumD = (k) => detailBoats.reduce((a, b) => a + (Number(b[k]) || 0), 0);
  const dCharged = sumD("charged");
  const dPaid = sumD("paid");
  const dDue = sumD("balance");

  const openDetails = (o) => {
    setPayForm({ amount: "", date: "", note: "", target: "split" });
    setDetailsOwner(o);
  };

  const addOwnerPayment = async () => {
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) return toast.error("Δώστε έγκυρο ποσό");
    const targets = payForm.target === "split" ? detailBoats : detailBoats.filter((b) => b.id === payForm.target);
    if (!targets.length) return toast.error("Επιλέξτε σκάφος");
    try {
      if (targets.length === 1) {
        await api.post(`/boats/${targets[0].id}/payments`, { amount: amt, date: payForm.date || undefined, note: payForm.note || undefined });
      } else {
        const n = targets.length;
        const cents = Math.round(amt * 100);
        const base = Math.floor(cents / n);
        const rem = cents - base * n;
        for (let i = 0; i < n; i++) {
          const share = (base + (i < rem ? 1 : 0)) / 100;
          const note = [payForm.note, "(επιμερισμός)"].filter(Boolean).join(" ");
          await api.post(`/boats/${targets[i].id}/payments`, { amount: share, date: payForm.date || undefined, note });
        }
      }
      await qc.invalidateQueries();
      toast.success("Καταχωρήθηκε πληρωμή");
      setPayForm({ amount: "", date: "", note: "", target: payForm.target });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  return (
    <div>
      <PageHeader
        title="Ιδιοκτήτες"
        subtitle={`${owners.length} ιδιοκτήτες`}
        action={
          <Button onClick={() => navigate("/boats?new=1")} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="add-owner-btn">
            <Plus size={18} weight="bold" /> Προσθήκη ιδιοκτήτη
          </Button>
        }
      />
      <div className="p-6">
        <div className="mb-4 max-w-md">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Αναζήτηση ιδιοκτήτη (όνομα ή σκάφος)…"
            className="rounded-sm"
            data-testid="owners-search-input"
          />
        </div>
        <Card className="rounded-sm border-border shadow-none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="owners-table">
              <thead>
                <tr className="border-b border-border text-left text-xs tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Ιδιοκτήτης</th>
                  <th className="px-4 py-3 font-semibold">Σκάφη</th>
                  <th className="px-4 py-3 text-right font-semibold">Χρέωση</th>
                  <th className="px-4 py-3 text-right font-semibold">Πληρωμές</th>
                  <th className="px-4 py-3 text-right font-semibold">Υπόλοιπο</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Φόρτωση...</td></tr>
                ) : owners.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">Δεν υπάρχουν ιδιοκτήτες ακόμη.</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">Δεν βρέθηκαν ιδιοκτήτες για «{search}».</td></tr>
                ) : (
                  filtered.map((o, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/50" data-testid={`owner-row-${i}`}>
                      <td className="px-4 py-3 font-medium">
                        <span>{o.owner}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {o.boats.map((b, bi) => (
                            <button
                              key={bi}
                              onClick={() => navigate(`/boats?focus=${b.id}`)}
                              className="inline-flex items-center gap-1 rounded-sm border border-border bg-secondary px-2 py-0.5 text-xs transition-colors duration-150 hover:border-[#0055FF] hover:bg-[#EFF6FF]"
                              data-testid={`owner-boat-${b.id}`}
                            >
                              <Boat size={12} weight="bold" className="text-[#0055FF]" /> {b.name}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{eur(o.total_charged)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#10B981]">{eur(o.total_paid)}</td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">{eur(o.total_due)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-black" onClick={() => openDetails(o)} data-testid={`details-owner-${i}`} title="Προβολή">
                            <CurrencyEur size={16} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-[#B91C1C]" onClick={() => window.open(reportUrl(`/reports/owner.pdf?owner=${encodeURIComponent(o.owner)}`), "_blank")} data-testid={`owner-pdf-${i}`} title="Λογαριασμός PDF">
                            <FilePdf size={16} weight="bold" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => setEditOwner({ old: o.owner, name: o.owner })} data-testid={`edit-owner-${i}`} title="Επεξεργασία">
                            <PencilSimple size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-destructive" onClick={() => setDeleteOwner(o.owner)} data-testid={`delete-owner-${i}`} title="Διαγραφή">
                            <Trash size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Owner details */}
      <Dialog open={!!detailsOwner} onOpenChange={(o) => !o && setDetailsOwner(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="owner-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{detailsOwner?.owner}</DialogTitle>
            <DialogDescription>Σύνοψη ιδιοκτήτη — {detailBoats.length} σκάφη</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            {detailBoats.map((b) => (
              <button
                key={b.id}
                onClick={() => { setDetailsOwner(null); navigate(`/boats?focus=${b.id}`); }}
                className="flex w-full items-center justify-between rounded-sm border border-border p-3 text-left transition-colors hover:border-[#0055FF] hover:bg-[#EFF6FF]"
                data-testid={`owner-detail-boat-${b.id}`}
              >
                <div>
                  <div className="font-medium">{b.name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><MapPin size={12} weight="bold" /> {b.slot_number}</span>
                    <span>{gr(b.start_date) || "—"} → {gr(b.end_date) || "—"}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-muted-foreground">Χρέωση έως σήμερα</div>
                  <div className="font-semibold tabular-nums">{eur(b.charged_to_date)}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 rounded-sm bg-secondary/60 p-3 text-center">
            <div>
              <div className="text-[11px] text-muted-foreground">Χρέωση</div>
              <div className="font-semibold tabular-nums">{eur(dCharged)}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">Πληρωμές</div>
              <div className="font-semibold tabular-nums text-[#10B981]">{eur(dPaid)}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">Υπόλοιπο</div>
              <div className="font-semibold tabular-nums">{eur(dDue)}</div>
            </div>
          </div>

          {/* Owner-level payment */}
          <div className="rounded-sm border border-border p-3" data-testid="owner-payment-box">
            <h4 className="font-head text-sm font-bold">Καταχώρηση πληρωμής</h4>
            {detailBoats.length > 1 && (
              <div className="mt-2">
                <Label className="text-xs text-muted-foreground">Αφορά</Label>
                <Select value={payForm.target} onValueChange={(v) => setPayForm((s) => ({ ...s, target: v }))}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="owner-pay-target"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="split">Επιμερισμός σε όλα τα σκάφη ({detailBoats.length})</SelectItem>
                    {detailBoats.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {payForm.target === "split" && Number(payForm.amount) > 0 && (
                  <p className="mt-1 text-[11px] text-muted-foreground" data-testid="owner-pay-split-hint">
                    ≈ {eur(Number(payForm.amount) / detailBoats.length)} ανά σκάφος
                  </p>
                )}
              </div>
            )}
            <div className="mt-2 flex items-end gap-2">
              <div className="w-24">
                <Label className="text-xs text-muted-foreground">Ποσό σε ευρώ</Label>
                <Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1 rounded-sm" data-testid="owner-pay-amount" />
              </div>
              <div className="w-40">
                <Label className="text-xs text-muted-foreground">Ημερομηνία</Label>
                <DateField value={payForm.date} onChange={(v) => setPayForm({ ...payForm, date: v })} testId="owner-pay-date" />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Σημείωση</Label>
                <Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: up(e.target.value) })} className="mt-1 rounded-sm" placeholder="προαιρετικό" data-testid="owner-pay-note" />
              </div>
              <Button size="icon" className="rounded-sm bg-[#10B981] hover:bg-[#0e9d70]" onClick={addOwnerPayment} data-testid="owner-pay-submit"><Plus size={16} weight="bold" /></Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm text-[#B91C1C]"
              onClick={() => window.open(reportUrl(`/reports/owner.pdf?owner=${encodeURIComponent(detailsOwner?.owner || "")}`), "_blank")}
              data-testid="owner-details-pdf"
            >
              <FilePdf size={16} weight="bold" /> Λογαριασμός PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit (rename) owner */}
      <Dialog open={!!editOwner} onOpenChange={(o) => !o && setEditOwner(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="owner-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Επεξεργασία ιδιοκτήτη</DialogTitle>
            <DialogDescription>Η αλλαγή θα εφαρμοστεί σε όλα τα σκάφη του ιδιοκτήτη.</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Label className="text-xs text-muted-foreground">Όνομα ιδιοκτήτη</Label>
            <Input
              value={editOwner?.name || ""}
              onChange={(e) => setEditOwner((s) => ({ ...s, name: up(e.target.value) }))}
              className="mt-1 rounded-sm"
              data-testid="owner-edit-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditOwner(null)}>Άκυρο</Button>
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={saveRename} data-testid="owner-edit-save">Αποθήκευση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete owner */}
      <AlertDialog open={!!deleteOwner} onOpenChange={(o) => !o && setDeleteOwner(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Διαγραφή ιδιοκτήτη;</AlertDialogTitle>
            <AlertDialogDescription>
              Θα διαγραφούν <b>όλα τα σκάφη</b> του ιδιοκτήτη «{deleteOwner}» και θα ελευθερωθούν οι θέσεις τους. Η ενέργεια δεν αναιρείται.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Άκυρο</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doDelete} data-testid="confirm-delete-owner-btn">Διαγραφή</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
